import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_89: DocMigration = {
  maxSchemaVersion: "0.0.89",
  description: "Generate Datums and Raised Slabs",
  apply(json: any) {
    migrateDoc0090GenerateDatums(json);
    return json;
  },
};

function migrateDoc0090GenerateDatums(json: any) {
  // update wall props to have datum anchors
  if (json.props) {
    for (const propId in json.props) {
      const prop = json.props[propId];
      if (prop.type === "WALL" && prop.entityType === "PROP") {
        prop.anchor = "LEVEL";
      }
    }
  }

  // migrate foundations
  if (json.settings) {
    if (json.settingsOverride) {
      migrateFoundationsGenerateDatums(json.settingsOverride, json.settings);
    }

    if (json.elevations) {
      for (const eid in json.elevations) {
        const elevation = json.elevations[eid];
        if (elevation.settingsOverride) {
          migrateFoundationsGenerateDatums(elevation.settingsOverride, json.settings);
        }
      }
    }
  }

  return json;
}

function migrateFoundationsGenerateDatums(settingsOverride, settings) {
  const foundationId = settingsOverride.foundation;
  let foundationSettings;

  let slabId = settingsOverride.foundationSlab;
  let slabSettings;

  let slabMonoId = settingsOverride.foundationSlabMonolithic;
  let slabMonoSettings;

  let slabStemId = settingsOverride.foundationSlabStemWall;
  let slabStemSettings;

  // utility method for creating new foundation system settings if needed

  function makeSlabSettings() {
    if (slabId) {
      slabSettings = settings[slabId];
    } else {
      slabId = uid();
      slabSettings = { type: "FOUNDATION_SLAB_SETTINGS" };
      settingsOverride.foundationSlab = slabId;
      settings[slabId] = slabSettings;
    }
  }

  function makeMonoSettings() {
    if (slabMonoId) {
      slabMonoSettings = settings[slabMonoId];
    } else {
      slabMonoId = uid();
      slabMonoSettings = { type: "FOUNDATION_SLAB_SETTINGS_MONO" };
      settingsOverride.foundationSlabMonolithic = slabMonoId;
      settings[slabMonoId] = slabMonoSettings;
    }
  }

  function makeStemSettings() {
    if (slabStemId) {
      slabStemSettings = settings[slabStemId];
    } else {
      slabStemId = uid();
      slabStemSettings = { type: "FOUNDATION_SLAB_SETTINGS_STEM_CMU" };
      settingsOverride.foundationSlabStemWallCMU = slabStemId;
      settings[slabStemId] = slabStemSettings;
    }
  }

  // this migration needs to operate from the bottom of the tree up,
  // to maintain the override heirarchy from older settings

  // update mono and stem settings

  if (slabMonoId) {
    slabMonoSettings = settings[slabMonoId];

    if (slabMonoId.foundationDepth !== undefined) {
      const foundationDepth = slabMonoId.foundationDepth;
      delete slabMonoId.foundationDepth;

      slabMonoId.foundationDatumOffset = foundationDepth;
    }

    if (slabMonoId.brickLedgeDepth !== undefined) {
      const brickLedgeDepth = slabMonoId.brickLedgeDepth;
      delete slabStemSettings.brickLedgeDepth;

      slabMonoId.brickLedgeDatumOffset = -brickLedgeDepth;
    }
  }

  if (slabStemId) {
    slabStemSettings = settings[slabStemId];

    if (slabStemSettings.stemWallWidth) {
      delete slabStemSettings.stemWallWidth;
    }

    if (slabStemSettings.stemWallInsetType) {
      delete slabStemSettings.stemWallInsetType;
    }

    if (slabStemSettings.stemWallInset) {
      delete slabStemSettings.stemWallInset;
    }

    if (slabStemSettings.foundationDepth !== undefined) {
      const foundationDepth = slabStemSettings.foundationDepth;
      delete slabStemSettings.foundationDepth;

      slabStemSettings.foundationDatumOffset = foundationDepth;
    }

    if (slabStemSettings.brickLedgeDepth !== undefined) {
      const brickLedgeDepth = slabStemSettings.brickLedgeDepth;
      delete slabStemSettings.brickLedgeDepth;

      slabStemSettings.brickLedgeDatumOffset = -brickLedgeDepth;
    }
  }

  // update slab settings
  if (slabId) {
    slabSettings = settings[slabId];
    if (slabSettings.foundationSystemType === "SLAB") {
      slabSettings.foundationSystemType === "SLAB_ON_GRADE";
    }

    if (slabSettings.foundationDepth !== undefined) {
      const foundationDepth = slabSettings.foundationDepth;
      delete slabSettings.foundationDepth;

      slabSettings.foundationDatumOffset = foundationDepth;
    }

    if (slabSettings.brickLedgeDepth !== undefined) {
      const brickLedgeDepth = slabSettings.brickLedgeDepth;
      delete slabSettings.brickLedgeDepth;

      slabSettings.brickLedgeDatumOffset = -brickLedgeDepth;
    }

    if (slabSettings.footingWidth !== undefined) {
      const footingWidth = slabSettings.footingWidth;
      delete slabSettings.footingWidth;

      makeStemSettings();
      makeMonoSettings();

      slabMonoSettings.footingWidth = slabMonoSettings.footingWidth ?? footingWidth;
      slabStemSettings.footingWidth = slabStemSettings.footingWidth ?? footingWidth;
    }

    if (slabSettings.spanFootingWidth !== undefined) {
      const spanFootingWidth = slabSettings.spanFootingWidth;
      delete slabSettings.spanFootingWidth;

      makeStemSettings();
      makeMonoSettings();

      slabMonoSettings.spanFootingWidth = slabMonoSettings.spanFootingWidth ?? spanFootingWidth;
      slabStemSettings.spanFootingWidth = slabStemSettings.spanFootingWidth ?? spanFootingWidth;
    }

    if (slabSettings.brickLedgeWidth !== undefined) {
      const brickLedgeWidth = slabSettings.brickLedgeWidth;
      delete slabSettings.brickLedgeWidth;

      makeMonoSettings();

      slabMonoSettings.brickLedgeWidth = slabMonoSettings.brickLedgeWidth ?? brickLedgeWidth;
    }
  }

  // update base foundation settings
  if (foundationId) {
    foundationSettings = settings[foundationId];
    if (foundationSettings.foundationSystemType === "SLAB") {
      foundationSettings.foundationSystemType === "SLAB_ON_GRADE";
    }

    if (foundationSettings.footingWidth !== undefined) {
      const footingWidth = foundationSettings.footingWidth;
      delete foundationSettings.footingWidth;

      makeStemSettings();
      makeMonoSettings();

      slabMonoSettings.footingWidth = slabMonoSettings.footingWidth ?? footingWidth;
      slabStemSettings.footingWidth = slabStemSettings.footingWidth ?? footingWidth;
    }

    if (foundationSettings.spanFootingWidth !== undefined) {
      const spanFootingWidth = foundationSettings.spanFootingWidth;
      delete foundationSettings.spanFootingWidth;

      makeStemSettings();
      makeMonoSettings();

      slabMonoSettings.spanFootingWidth = slabMonoSettings.spanFootingWidth ?? spanFootingWidth;
      slabStemSettings.spanFootingWidth = slabStemSettings.spanFootingWidth ?? spanFootingWidth;
    }

    if (foundationSettings.foundationDepth !== undefined) {
      const foundationDepth = foundationSettings.foundationDepth;
      delete foundationSettings.foundationDepth;

      makeSlabSettings();

      // this is a rename as well as a migration
      slabSettings.foundationDatumOffset = slabSettings.foundationDepth ?? foundationDepth;
      delete slabSettings.foundationDepth;
    }

    if (foundationSettings.brickLedgeDepth !== undefined) {
      const brickLedgeDepth = foundationSettings.brickLedgeDepth;
      delete foundationSettings.brickLedgeDepth;

      makeSlabSettings();

      // this is a rename as well as a migration
      slabSettings.brickLedgeDatumOffset = slabSettings.brickLedgeDepth ?? -brickLedgeDepth;
      delete slabSettings.brickLedgeDepth;
    }

    if (foundationSettings.brickLedgeWidth !== undefined) {
      const brickLedgeWidth = foundationSettings.brickLedgeWidth;
      delete foundationSettings.brickLedgeWidth;

      makeMonoSettings();

      slabMonoSettings.brickLedgeWidth = slabMonoSettings.brickLedgeWidth ?? brickLedgeWidth;
    }
  }

  // move porch offset to foundation
  if (settingsOverride.porch) {
    const pid = settingsOverride.porch;
    const porchSettings = settings[pid];

    // this was never used, so can be cleanly removed if it exists
    delete porchSettings.porchDatum;

    if (porchSettings.porchDatumOffset !== undefined) {
      const porchDatumOffset = porchSettings.porchDatumOffset;
      delete porchSettings.porchDatumOffset;

      makeSlabSettings();

      slabSettings.porchDatumOffset = porchDatumOffset;
    }
  }

  // move garage offset and curb to foundation
  if (settingsOverride.garage) {
    const gid = settingsOverride.garage;
    const garageSettings = settings[gid];

    // this was never used, so can be cleanly removed if it exists
    delete garageSettings.garageDatum;

    if (garageSettings.porchDatumOffset !== undefined) {
      const garageDatumOffset = garageSettings.garageDatumOffset;
      delete garageSettings.porchDatumOffset;

      makeSlabSettings();

      slabSettings.garageDatumOffset = garageDatumOffset;
    }

    if (garageSettings.garageCurbWidth !== undefined) {
      const garageCurbWidth = garageSettings.garageCurbWidth;
      delete garageSettings.garageCurbWidth;

      makeMonoSettings();

      slabMonoSettings.garageCurbWidth = garageCurbWidth;
    }
  }

  // cleanup settings
  // only the type property will exist on an empty settings object

  if (foundationSettings && Object.keys(foundationSettings).length <= 1) {
    delete settingsOverride.foundation;
    delete settings[foundationId];
  }

  if (slabSettings && Object.keys(slabSettings).length <= 1) {
    delete settingsOverride.foundationSlab;
    delete settings[slabId];
  }

  if (slabMonoSettings && Object.keys(slabMonoSettings).length <= 1) {
    delete settingsOverride.foundationSlabMonolithic;
    delete settings[slabMonoId];
  }

  if (slabStemSettings && Object.keys(slabStemSettings).length <= 1) {
    delete settingsOverride.foundationSlabStemWallCMU;
    delete settings[slabStemId];
  }
}

export default migration_0_0_89;
